import { DefaultNumberFieldComponent } from "@constellation-academy/framework-react-components";

import { useFormik } from "formik";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Suspense, useRef } from "react";
import { useSelector } from "react-redux";
import { useFragment, useMutation } from "react-relay";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { licenseProductForm_EditLicenseProductMutation } from "@relay/licenseProductForm_EditLicenseProductMutation.graphql";
import { licenseProductForm_EditProductMutation } from "@relay/licenseProductForm_EditProductMutation.graphql";
import { licenseProductForm_LicenseProductFragment$key } from "@relay/licenseProductForm_LicenseProductFragment.graphql";
import { licenseProductForm_QueryFragment$key } from "@relay/licenseProductForm_QueryFragment.graphql";
import { LicenseProductDataContentKind } from "@relay/ProductTypeColumn_ProductFragment.graphql";
import {
	QUERY_FRAGMENT,
	EDIT_PRODUCT_MUTATION,
	LICENSE_PRODUCT_FRAGMENT,
	EDIT_LICENSE_PRODUCT_MUTATION,
} from "./license-product-form.graphql";
import {
	contentKindOptions,
	LicenseProductFormProps,
	LicenseProductFormState,
} from "./license-product-form.types";
import { ValidatedField } from "../../../../components/ValidatedField";
import {
	SelectLicenseDefinitionField,
	SelectLicenseDefinitionFieldState,
} from "../../../../features/license-definitions/select-license-definition-field";
import { selectActiveFeatureToggles } from "../../../../store/slices/FeatureTogglesSlice";
import {
	ConfigDropdown,
	ConfigDropdownOptions,
} from "../../../educational-offer/parts/ConfigDropdown";
import { ProductForm } from "../product-form/product-form.component";

export const LicenseProductForm = ({
	productFragmentRef,
	queryFragmentRef,
}: LicenseProductFormProps) => {
	const { t } = useTranslation("common");
	const toast = useRef<Toast>(null);
	const activeFeatureToggles = useSelector(selectActiveFeatureToggles);

	const query = useFragment<licenseProductForm_QueryFragment$key>(
		QUERY_FRAGMENT,
		queryFragmentRef,
	);

	const product = useFragment<licenseProductForm_LicenseProductFragment$key>(
		LICENSE_PRODUCT_FRAGMENT,
		productFragmentRef,
	);

	const [editProduct] =
		useMutation<licenseProductForm_EditProductMutation>(EDIT_PRODUCT_MUTATION);
	const [editLicenseProduct] = useMutation<licenseProductForm_EditLicenseProductMutation>(
		EDIT_LICENSE_PRODUCT_MUTATION,
	);

	const hasSplitCourseCertification = activeFeatureToggles?.includes("splitCourseCertification");

	const formik = useFormik<LicenseProductFormState>({
		enableReinitialize: true,
		initialValues: {
			title: product.title,
			isHidden: product.isHidden,
			isTaxFree: product.isTaxFree,
			netPrice: product.netPrice,
			licenseDefinition: product.data.licenseDefinition
				? {
						id: product.data.licenseDefinition.id,
						name: product.data.licenseDefinition.data.name,
				  }
				: undefined,
			licenseDefinitionAmount: product.data.licenseDefinitionAmount ?? 1,
			contentKind: hasSplitCourseCertification
				? product.data?.content?.kind ?? ""
				: undefined,
		},
		validationSchema: Yup.object().shape({
			title: Yup.string().required(t("product.titleRequired")),
			netPrice: Yup.number().required(t("product.netPriceRequired")),
			isHidden: Yup.boolean(),
			licenseDefinition: Yup.object()
				.default(undefined)
				.required(t("product.licenseRequired")),
			licenseDefinitionAmount: Yup.number()
				.min(1, t("product.minOneLicense"))
				.typeError(t("product.licenseAmountNumber")),
			contentKind: hasSplitCourseCertification
				? Yup.string().required()
				: Yup.mixed().notRequired(),
		}),
		onSubmit: (values: LicenseProductFormState, { setSubmitting }) => {
			editProduct({
				variables: {
					input: {
						productId: product.id,
						newNetPrice: values.netPrice,
						newTitle: values.title,
						newIsHidden: values.isHidden ?? true,
						newIsTaxFree: values.isTaxFree,
					},
				},
				onCompleted: () => {
					const licenseInput: licenseProductForm_EditLicenseProductMutation["variables"]["input"] =
						{
							id: product.id,
							licenseDefinitionId: values.licenseDefinition!.id,
							licenseDefinitionAmount: values.licenseDefinitionAmount,
						};

					if (hasSplitCourseCertification && values.contentKind) {
						licenseInput.content = {
							[values.contentKind]: {
								kind: values.contentKind,
							},
						};
					}

					editLicenseProduct({
						variables: {
							input: licenseInput,
						},
						onCompleted: () => {
							setSubmitting(false);
						},
					});
				},
			});
		},
	});

	const options: Array<ConfigDropdownOptions<LicenseProductDataContentKind>> =
		contentKindOptions.map((c) => {
			return {
				label: t(`product.${c}`),
				value: c,
			};
		});
	return (
		<>
			<Toast ref={toast} />
			<Suspense fallback={<ProgressSpinner />}>
				<ProductForm formik={formik} handleSubmit={formik.handleSubmit}>
					<ValidatedField<LicenseProductFormState, SelectLicenseDefinitionFieldState>
						required
						name={"licenseDefinition"}
						label={t("product.licenseDefinitionLabel")}
						placeholder={t("product.licenseDefinitionPlaceholder")}
						formikConfig={formik}
						component={(renderConfig) => (
							<SelectLicenseDefinitionField
								{...renderConfig}
								queryFragmentRef={query}
							/>
						)}
					/>
					<ValidatedField<LicenseProductFormState, number>
						name={"licenseDefinitionAmount"}
						label={t("product.licenseAmount")}
						component={DefaultNumberFieldComponent}
						formikConfig={formik}
					/>
					{hasSplitCourseCertification && (
						<ValidatedField<LicenseProductFormState, LicenseProductDataContentKind>
							required
							name="contentKind"
							label={t("product.contentKind")}
							formikConfig={formik}
							component={() => {
								const value = formik.values.contentKind;
								return (
									<ConfigDropdown<LicenseProductDataContentKind>
										configOptions={options}
										value={value}
										onChange={(e) => {
											formik.setFieldValue("contentKind", e.value);
										}}
										isPresentational={false}
									/>
								);
							}}
						/>
					)}
				</ProductForm>
			</Suspense>
		</>
	);
};
